(() => {
  const elementName = 'ug-jp-spokesperson-series';
  if (customElements.get(elementName)) return;

  class SpokespersonSeries extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.frame = 0;
      this.measureFrame = 0;
      this.wakeTimer = 0;
      this.lastTime = 0;
      this.editorBlock = null;
      this.tabs = Array.from(this.querySelectorAll('[data-series-tab]'));
      this.panels = this.tabs.map((tab) => {
        const id = tab.getAttribute('aria-controls');
        return Array.from(this.querySelectorAll('[data-series-panel]')).find((panel) => panel.id === id);
      });
      this.states = this.panels.map((panel) => {
        const rail = panel?.querySelector('.ug-jps__rail');
        const track = rail?.querySelector('.ug-jps__track');
        return { panel, rail, track, clones: [], span: 0, width: 0, position: 0, hovered: false, visible: false, pointerId: null, manualUntil: 0, expected: 0, programmatic: false };
      });
      this.speed = Math.max(0, Number(this.dataset.scrollSpeed ?? 32) || 0);
      this.autoplay = this.dataset.autoplay === 'true' && this.speed > 0;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const signal = this.controller.signal;
      const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal });

      listen(this, 'click', (event) => {
        if (event.target.closest('[data-tabs-next]') && this.tabs.length) {
          this.select((this.active + 1) % this.tabs.length, 'click');
          return;
        }
        const tab = event.target.closest('[data-series-tab]');
        const index = this.tabs.indexOf(tab);
        if (index !== -1) this.select(index, 'click');
      });
      listen(this, 'keydown', (event) => {
        const index = this.tabs.indexOf(event.target);
        if (index === -1) return;
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        this.select(next, 'keyboard');
        this.tabs[next].focus({ preventScroll: true });
      });
      listen(this, 'focusin', (event) => {
        this.reconcile();
        const card = event.target.closest('[data-product-card]');
        if (card && !card.closest('[data-series-clone]')) {
          card.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
        }
      });
      listen(this, 'focusout', () => queueMicrotask(() => this.reconcile()));
      listen(document, 'visibilitychange', () => this.reconcile());
      listen(this.motion, 'change', () => this.measure());
      const releasePointer = (event) => {
        this.states.forEach((state) => {
          if (state.pointerId !== event.pointerId) return;
          state.pointerId = null;
          state.manualUntil = performance.now() + 6000;
        });
        this.reconcile();
      };
      listen(window, 'pointerup', releasePointer, { passive: true });
      listen(window, 'pointercancel', releasePointer, { passive: true });
      listen(document, 'shopify:block:select', (event) => this.selectEditorBlock(event));
      listen(document, 'shopify:block:deselect', (event) => {
        if (String(event.detail?.blockId) !== this.editorBlock) return;
        this.editorBlock = null;
        this.reconcile();
      });

      this.states.forEach((state) => {
        if (!state.rail || !state.track) return;
        const pause = () => {
          state.manualUntil = performance.now() + 6000;
          this.reconcile();
        };
        listen(state.rail, 'pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          state.hovered = true;
          this.reconcile();
        });
        listen(state.rail, 'pointerleave', () => {
          state.hovered = false;
          this.reconcile();
        });
        listen(state.rail, 'pointerdown', (event) => {
          state.pointerId = event.pointerId;
          pause();
        }, { passive: true });
        listen(state.rail, 'wheel', pause, { passive: true });
        listen(state.rail, 'scroll', () => {
          if (state.programmatic) {
            state.expected = state.rail.scrollLeft;
            return;
          }
          if (Math.abs(state.rail.scrollLeft - state.expected) > 1) {
            state.position = state.rail.scrollLeft;
            pause();
          }
          state.expected = state.rail.scrollLeft;
        }, { passive: true });
      });

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const state = this.states.find((item) => item.rail === entry.target);
            if (state) state.visible = entry.isIntersecting;
          });
          this.reconcile();
        });
        this.states.forEach((state) => {
          if (state.rail) this.intersectionObserver.observe(state.rail);
        });
      } else {
        listen(window, 'scroll', () => {
          this.updateVisibility();
          this.reconcile();
        }, { passive: true });
      }
      const scheduleMeasure = () => {
        if (this.measureFrame) return;
        this.measureFrame = requestAnimationFrame(() => {
          this.measureFrame = 0;
          this.measure();
        });
      };
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(scheduleMeasure);
        this.states.forEach((state) => {
          if (!state.rail || !state.track) return;
          this.resizeObserver.observe(state.rail);
          this.resizeObserver.observe(state.track);
        });
      }
      listen(window, 'resize', scheduleMeasure, { passive: true });
      listen(this, 'load', scheduleMeasure, { capture: true });
      const initial = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.select(Math.max(0, initial));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.measureFrame);
      clearTimeout(this.wakeTimer);
      this.states?.forEach((state) => this.removeClones(state));
    }

    select(index, source = 'programmatic') {
      if (!this.panels[index]) return;
      const previous = this.active;
      const previousPanel = this.panels[previous];
      this.active = index;
      this.tabs.forEach((tab, position) => {
        const selected = position === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (this.panels[position]) this.panels[position].hidden = !selected;
      });
      this.lastTime = 0;
      this.measure();
      this.revealTab(this.tabs[index]);
      const tracking = window.UGJPTracking;
      if (previousPanel && previous !== index && (source === 'click' || source === 'keyboard') && tracking && tracking.allowed()) {
        tracking.track('jp_sp_tab_change', {
          ...tracking.context(this.panels[index]),
          from_series_id: previousPanel.dataset.jpSeriesId || '',
          series_id: this.panels[index].dataset.jpSeriesId || ''
        });
      }
    }

    revealTab(tab) {
      const list = tab.closest('.ug-jps__tabs');
      if (!list) return;
      const tabRect = tab.getBoundingClientRect();
      const listRect = list.getBoundingClientRect();
      const nextRect = this.querySelector('[data-tabs-next]')?.getBoundingClientRect();
      const right = nextRect?.width ? Math.min(listRect.right, nextRect.left) : listRect.right;
      let left = list.scrollLeft;
      if (tabRect.left < listRect.left || tabRect.width > right - listRect.left) {
        left += tabRect.left - listRect.left;
      } else if (tabRect.right > right) {
        left += tabRect.right - right;
      }
      if (Math.abs(list.scrollLeft - left) > 1) {
        list.scrollTo({ left, behavior: this.motion.matches ? 'auto' : 'smooth' });
      }
    }

    selectEditorBlock(event) {
      const blockId = String(event.detail?.blockId ?? '');
      let card;
      const index = this.states.findIndex((state) => {
        if (state.panel?.dataset.blockId === blockId) return true;
        card = Array.from(state.track?.querySelectorAll('[data-product-card]') || []).find((item) => item.dataset.blockId === blockId);
        return Boolean(card);
      });
      if (index === -1) return;
      this.editorBlock = blockId;
      this.select(index);
      if (card) card.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
    }

    removeClones(state) {
      state.clones.forEach((clone) => clone.remove());
      state.clones = [];
      state.span = 0;
      state.width = 0;
    }

    cloneTrack(state) {
      const clone = state.track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-series-clone', '');
      clone.classList.add('ug-jps__track--clone');
      [clone, ...clone.querySelectorAll('*')].forEach((node) => {
        ['id', 'data-block-id', 'data-shopify-editor-block', 'autofocus', 'aria-labelledby', 'aria-describedby', 'aria-controls', 'for'].forEach((attribute) => node.removeAttribute(attribute));
        if (node.matches('a, button, input, select, textarea, summary, [tabindex], [contenteditable]')) node.tabIndex = -1;
        if (node.hasAttribute('contenteditable')) node.setAttribute('contenteditable', 'false');
      });
      state.rail.appendChild(clone);
      state.clones.push(clone);
    }

    updateVisibility() {
      this.states.forEach((state) => {
        if (!state.rail) return;
        const rect = state.rail.getBoundingClientRect();
        state.visible = !state.panel.hidden && rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
      });
    }

    measure() {
      if (!this.controller) return;
      this.states.forEach((state, index) => {
        if (!state.rail || !state.track) return;
        if (!this.autoplay || this.motion.matches) {
          this.removeClones(state);
          return;
        }
        if (index !== this.active) return;
        const width = state.rail.clientWidth;
        const trackWidth = state.track.getBoundingClientRect().width;
        const railGap = parseFloat(getComputedStyle(state.rail).columnGap);
        const cloneGap = state.clones[0] ? parseFloat(getComputedStyle(state.clones[0]).marginLeft) : NaN;
        const gap = Number.isFinite(railGap) && railGap > 0 ? railGap : (Number.isFinite(cloneGap) && cloneGap > 0 ? cloneGap : 16);
        const span = trackWidth + gap;
        if (!width || !trackWidth) return;
        if (Math.abs(state.span - span) < 0.5 && state.width === width && state.clones.length) return;
        const position = state.rail.scrollLeft;
        this.removeClones(state);
        state.span = span;
        state.width = width;
        const copies = Math.min(12, Math.ceil(width / span) + 1);
        for (let count = 0; count < copies; count += 1) this.cloneTrack(state);
        this.setRailScroll(state, position % span);
      });
      this.updateVisibility();
      this.reconcile();
    }

    setRailScroll(state, position) {
      const rail = state.rail;
      // Keep subpixel progress: Safari may round the DOM scroll offset to an integer.
      state.position = position;
      state.programmatic = true;
      if (typeof rail.scrollTo === 'function') {
        rail.scrollTo(position, 0);
      } else {
        rail.scrollLeft = position;
      }
      if (Math.abs(rail.scrollLeft - position) > 1) {
        rail.scrollLeft = position;
      }
      state.expected = rail.scrollLeft;
      requestAnimationFrame(() => {
        state.programmatic = false;
      });
    }

    reconcile() {
      if (!this.controller) return;
      cancelAnimationFrame(this.frame);
      clearTimeout(this.wakeTimer);
      this.frame = 0;
      this.wakeTimer = 0;
      const state = this.states[this.active];
      const focused = document.activeElement;
      const focusPaused = Boolean(
        focused
        && this.contains(focused)
        && focused.closest('.ug-jps__rail')
        && focused.matches(':focus-visible')
      );
      if (!state || !this.autoplay || this.motion.matches || document.hidden || !state.visible || !state.span || state.hovered || state.pointerId !== null || focusPaused || this.editorBlock) {
        this.lastTime = 0;
        return;
      }
      const remaining = state.manualUntil - performance.now();
      if (remaining > 0) {
        this.lastTime = 0;
        this.wakeTimer = setTimeout(() => this.reconcile(), remaining + 20);
        return;
      }
      this.frame = requestAnimationFrame((time) => this.tick(time));
    }

    tick(time) {
      this.frame = 0;
      if (!this.controller) return;
      const state = this.states[this.active];
      if (!state?.span) return;
      const elapsed = this.lastTime ? Math.min(time - this.lastTime, 50) : 0;
      this.lastTime = time;
      this.setRailScroll(state, (state.position + this.speed * elapsed / 1000) % state.span);
      this.frame = requestAnimationFrame((next) => this.tick(next));
    }
  }

  customElements.define(elementName, SpokespersonSeries);
})();
